// ════════════════════════════════════════════════════════════════════════════
// LpaBadge — pill colorido para status/categoria
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export type LpaBadgeVariant = "neutral" | "success" | "warning" | "danger" | "info" | "accent";
export type LpaBadgeSize = "sm" | "md";

export interface LpaBadgeProps {
  variant?: LpaBadgeVariant;
  size?: LpaBadgeSize;
  /** Ícone à esquerda (Unicode/SVG) — sempre aria-hidden */
  icon?: React.ReactNode;
  /** Para color-blind safety: shape complementar (▲▼—) */
  shape?: "up" | "down" | "neutral" | "circle" | null;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

function variantStyle(variant: LpaBadgeVariant): React.CSSProperties {
  switch (variant) {
    case "success":
      return { background: "var(--lpa-success-bg)", color: "var(--lpa-success)" };
    case "warning":
      return { background: "var(--lpa-warning-bg)", color: "var(--lpa-warning)" };
    case "danger":
      return { background: "var(--lpa-danger-bg)", color: "var(--lpa-danger)" };
    case "info":
      return { background: "var(--lpa-info-bg)", color: "var(--lpa-info)" };
    case "accent":
      return { background: "var(--lpa-accent-soft)", color: "var(--lpa-accent)" };
    case "neutral":
    default:
      return { background: "var(--lpa-surface)", color: "var(--lpa-muted)" };
  }
}

const SHAPE_GLYPH: Record<NonNullable<LpaBadgeProps["shape"]>, string> = {
  up: "▲",
  down: "▼",
  neutral: "—",
  circle: "●",
};

export default function LpaBadge({
  variant = "neutral",
  size = "sm",
  icon,
  shape = null,
  children,
  className,
  style,
}: LpaBadgeProps): React.ReactElement {
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: size === "sm" ? "2px 8px" : "4px 10px",
        fontSize: size === "sm" ? "var(--lpa-text-2xs)" : "var(--lpa-text-sm)",
        fontWeight: 600,
        fontFamily: "var(--lpa-font-mono)",
        borderRadius: "var(--lpa-radius-pill)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
        ...variantStyle(variant),
        ...style,
      }}
    >
      {(icon || shape) && (
        <span aria-hidden="true" style={{ display: "inline-flex" }}>
          {icon ?? (shape ? SHAPE_GLYPH[shape] : null)}
        </span>
      )}
      {children}
    </span>
  );
}
